import React from 'react';
import RolesGrid from './RolesGrid';
import Pill from '../interaction/Pill';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Tooltip,
  Autocomplete,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import PageHeader from '../interaction/PageHeader';

export default function SignupRequestManagerDesktop({
  pendingRequests,
  historyRequests,
  companies,
  roles,
  loading,
  error,
  success,
  setError,
  tabValue,
  setTabValue,
  reviewDialog,
  reviewData,
  setReviewData,
  reviewErrors,
  setReviewErrors,
  submitting,
  rejectDialog,
  setRejectDialog,
  rejectReason,
  setRejectReason,
  handleOpenReview,
  handleCloseReview,
  handleApprove,
  handleRoleToggle,
  handleOpenReject,
  handleReject,
  getCompanyOptions,
  formatRelativeTime,
  CREATE_NEW_COMPANY,
}) {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Signup Requests"
        subtitle="People who asked for access. Approving one pre-creates their user so they can sign in."
        className="ph--page"
      />
      <Card>
        <CardContent>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 3 }}>
            <Tab label={`Pending (${pendingRequests.length})`} />
            <Tab label="History" />
          </Tabs>

          {tabValue === 0 && (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Display Name</TableCell>
                    <TableCell>Proposed Company</TableCell>
                    <TableCell>Brands</TableCell>
                    <TableCell>Submitted</TableCell>
                    <TableCell>Message</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingRequests.map((req) => (
                    <TableRow key={req.id}>
                      <TableCell>{req.email}</TableCell>
                      <TableCell>{req.displayName || '-'}</TableCell>
                      <TableCell>{req.companyName || '-'}</TableCell>
                      <TableCell>
                        {req.brands?.length > 0 ? (
                          <Box display="flex" gap={0.5} flexWrap="wrap">
                            {req.brands.map((brand, i) => (
                              <Pill key={i} label={brand} variant="light" size="xs" />
                            ))}
                          </Box>
                        ) : '-'}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatRelativeTime(req.createdAt)}
                      </TableCell>
                      <TableCell>
                        {req.message ? (
                          <Tooltip title={req.message}>
                            <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {req.message}
                            </Typography>
                          </Tooltip>
                        ) : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <Box display="flex" gap={1} justifyContent="flex-end">
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<CheckCircleIcon />}
                            onClick={() => handleOpenReview(req)}
                          >
                            Review
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<CancelIcon />}
                            onClick={() => handleOpenReject(req)}
                          >
                            Reject
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {pendingRequests.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Box py={4}>
                          <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                          <Typography variant="body1" color="text.secondary">
                            No pending signup requests.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {tabValue === 1 && (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Display Name</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell>Brands</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Reviewed</TableCell>
                    <TableCell>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyRequests.map((req) => (
                    <TableRow key={req.id}>
                      <TableCell>{req.email}</TableCell>
                      <TableCell>{req.displayName || '-'}</TableCell>
                      <TableCell>
                        {req.status === 'approved'
                          ? req.resolution?.companyId || req.companyName
                          : req.companyName}
                      </TableCell>
                      <TableCell>
                        {req.brands?.length > 0 ? (
                          <Box display="flex" gap={0.5} flexWrap="wrap">
                            {req.brands.map((brand, i) => (
                              <Pill key={i} label={brand} variant="light" size="xs" />
                            ))}
                          </Box>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <Pill
                          label={req.status}
                          variant="light"
                          size="xs"
                          className={req.status === 'approved' ? 'pill--status-success' : 'pill--status-error'}
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatRelativeTime(req.reviewedAt)}
                      </TableCell>
                      <TableCell>
                        {req.status === 'approved' && req.resolution?.companyCreated && (
                          <Pill label="New company created" variant="light" size="xs" />
                        )}
                        {req.status === 'rejected' && req.rejectionReason && (
                          <Tooltip title={req.rejectionReason}>
                            <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {req.rejectionReason}
                            </Typography>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {historyRequests.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Box py={4}>
                          <Typography variant="body1" color="text.secondary">
                            No reviewed requests yet.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Review / Approve Dialog */}
      <Dialog open={reviewDialog.open} onClose={handleCloseReview} maxWidth="sm" fullWidth>
        <DialogTitle>Review Signup Request</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Email Address"
              value={reviewDialog.request?.email || ''}
              margin="normal"
              disabled
              helperText="Cannot be changed"
            />

            {reviewDialog.request?.brands?.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">Brands</Typography>
                <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
                  {reviewDialog.request.brands.map((brand, i) => (
                    <Pill key={i} label={brand} variant="light" size="xs" />
                  ))}
                </Box>
              </Box>
            )}

            <TextField
              fullWidth
              label="Display Name"
              value={reviewData.displayName}
              onChange={(e) => setReviewData({ ...reviewData, displayName: e.target.value })}
              margin="normal"
              helperText="Editable — defaults to what the user entered"
            />

            <Autocomplete
              fullWidth
              options={getCompanyOptions()}
              getOptionLabel={(option) => option.name || ''}
              value={reviewData.company}
              onChange={(event, newValue) => {
                setReviewData({ ...reviewData, company: newValue });
                setReviewErrors({ ...reviewErrors, company: '' });
              }}
              onInputChange={(event, value, reason) => {
                if (reason === 'input') {
                  setReviewData((prev) => ({ ...prev, newCompanyName: value }));
                }
              }}
              filterOptions={(options, { inputValue }) => {
                const filtered = options.filter(
                  (opt) => !opt._isNew && opt.name.toLowerCase().includes(inputValue.toLowerCase())
                );
                if (inputValue && !options.find(
                  (opt) => !opt._isNew && opt.name.toLowerCase() === inputValue.toLowerCase()
                )) {
                  filtered.push({
                    id: CREATE_NEW_COMPANY,
                    name: `+ Create new company: ${inputValue}`,
                    _isNew: true,
                    _newName: inputValue,
                  });
                }
                return filtered;
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Company"
                  margin="normal"
                  required
                  error={!!reviewErrors.company}
                  helperText={reviewErrors.company || `Proposed: "${reviewDialog.request?.companyName || ''}"`}
                  placeholder="Search or type to create"
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
            />

            <Box sx={{ mt: 2 }}>
              <Box sx={{ fontSize: '12px', fontWeight: 600, color: reviewErrors.roles ? 'error.main' : 'text.secondary', mb: 0.5 }}>
                Roles *
              </Box>
              {reviewErrors.roles && (
                <Box sx={{ fontSize: '11px', color: 'error.main', mb: 1 }}>{reviewErrors.roles}</Box>
              )}
              <RolesGrid
                allRoles={roles}
                selectedRoleIds={reviewData.roles}
                onToggle={handleRoleToggle}
              />
            </Box>

            {reviewDialog.request?.message && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Message from applicant:</Typography>
                <Typography variant="body2">{reviewDialog.request.message}</Typography>
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            color="error"
            onClick={() => {
              handleCloseReview();
              handleOpenReject(reviewDialog.request);
            }}
          >
            Reject Instead
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={handleCloseReview}>Cancel</Button>
          <Button
            onClick={handleApprove}
            variant="contained"
            disabled={submitting || !reviewData.company || !reviewData.roles.length}
          >
            {submitting ? <CircularProgress size={20} /> : 'Approve & Create User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onClose={() => setRejectDialog({ open: false, request: null })} maxWidth="xs" fullWidth>
        <DialogTitle>Reject Signup Request</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Reject the request from <strong>{rejectDialog.request?.email}</strong>?
          </Typography>
          <TextField
            fullWidth
            label="Reason (optional)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            margin="normal"
            multiline
            rows={3}
            inputProps={{ maxLength: 500 }}
            helperText="Not sent to the user — for internal records only"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog({ open: false, request: null })}>Cancel</Button>
          <Button
            onClick={handleReject}
            variant="contained"
            color="error"
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={20} /> : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
